import React, { useEffect } from 'react';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import PlusBanner from 'components/Plus/PlusBanner/PlusBanner';
import PlusBenefits from 'components/Plus/PlusBenefits/PlusBenefits';
import PlusSavings from 'components/Plus/PlusSavings/PlusSavings';
import PlusMembershipInfo from 'components/Plus/PlusMembershipInfo/PlusMembershipInfo';
import Help from 'components/Plus/Help/Help';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import PlusStatusBanner from 'components/Plus/PlusStatusBanner/PlusStatusBanner';
import { setCancellationFlowStep } from 'store/dashboard/slice';
import { CancellationFlowStep } from 'store/dashboard/types';
import CancellationFlow from 'components/Plus/CancellationFlow/CancellationFlow';
import addOPPlusSubscriptionAndRedirectToCartOperation from 'store/app/operation/addOPPlusSubscriptionAndRedirectToCartOperation';
import PlusNonMember, { usePromotionalContent } from 'components/Plus/PlusNonMember/PlusNonMember';
import getCancellationFlowStep from 'store/dashboard/selector/getCancellationFlowStep';
import usePlusMembershipStatus from 'components/Plus/hooks/usePlusMembershipStatus';
import getConfigurationByKey from 'store/app/selector/getConfigurationByKey';
import getOpPlusV3Info from 'store/dashboard/selector/getOpPlusV3Info';
import fetchPlusMembershipDataOperation from 'store/dashboard/operation/fetchPlusMembershipDataOperation';
import fetchRewardsSummaryOperation from 'store/dashboard/operation/fetchRewardsSummaryOperation';
import RewardsSection from 'components/Plus/RewardsSection/RewardsSection';
import isDesktop from 'store/app/selector/isDesktop';
import OpPlusCampaignDashboard from 'components/Common/op-ui-wrappers/OpPlusCampaignDashboard/OpPlusCampaignDashboard';
import styles from './PlusMembershipPage.module.scss';

const PlusMembershipPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const subscriptionData = useSelector(getOpPlusV3Info);
  const step = useSelector(getCancellationFlowStep);
  const { legacyExpiringSoon, isCancelled, isCancelledBeforeRenewal, isFailedPaymentCancelled } =
    usePlusMembershipStatus();
  const customerSupportUrl = useSelector(getConfigurationByKey('customerCareUrl'));
  const isDesktopDevice = useSelector(isDesktop);
  const promotionalContent = usePromotionalContent(isDesktopDevice);

  useEffect(() => {
    dispatch(fetchPlusMembershipDataOperation());
    dispatch(fetchRewardsSummaryOperation());
  }, []);

  const initiateCancellationFlow = () => {
    dispatch(setCancellationFlowStep(CancellationFlowStep.confirmation));
  };

  const addOPPlus = () => {
    dispatch(addOPPlusSubscriptionAndRedirectToCartOperation());
  };

  const redirectToCustomerSupport = () => {
    window.open(customerSupportUrl, '_blank');
  };

  if (!subscriptionData) {
    return null;
  }

  // render logic for NON PLUS CUSTOMER
  if (subscriptionData?.contentType === 'nonPlusCustomer') {
    return (
      <div className={styles.container}>
        <OpPlusCampaignDashboard />
        <PlusNonMember />
      </div>
    );
  }

  const shouldShowCancellationFlow = step !== CancellationFlowStep.initial;

  let plusBanner =
    subscriptionData.subscriptionInfo?.isActiveOpPlusMember &&
    subscriptionData.subscriptionInfo?.status &&
    !isCancelled &&
    !isCancelledBeforeRenewal &&
    !legacyExpiringSoon ? (
      <PlusBanner />
    ) : (
      <PlusStatusBanner />
    );

  // banner under developement, disabled to unlock release
  if (isFailedPaymentCancelled) {
    plusBanner = <></>; // <PlusRepurchaseBanner />;
  }

  return (
    <div className={styles.container}>
      {shouldShowCancellationFlow && <CancellationFlow />}
      {!shouldShowCancellationFlow && (
        <>
          <OpPlusCampaignDashboard />
          {plusBanner}
          <section className={styles.section}>
            <PlusMembershipInfo
              onCancelMembership={initiateCancellationFlow}
              onRepurchaseMembership={addOPPlus}
              onContactCustomerSupport={redirectToCustomerSupport}
            />
          </section>
          <section className={styles.section}>
            <OpText variant="title-large-bold" className={styles.title}>
              {t('txt_plus_benefits')}
            </OpText>
            <div className={cn(styles.sectionContent, styles.benefits)}>
              <PlusBenefits size="small" layout="row" />
              <PlusSavings showRepurchaseButton={isFailedPaymentCancelled} />
            </div>
          </section>
          {/* TODO: Uncomment once API for payment method is done */}
          {/* <section className={styles.section}>
            <PlusMembershipPaymentMethod />
          </section> */}

          <RewardsSection />
          {isFailedPaymentCancelled && (
            <>
              <div className={styles.promotionalContainer}>{promotionalContent}</div>
              <div className={styles.hr} />
            </>
          )}

          <section className={styles.section}>
            <OpText variant="title-large-bold" className={styles.title}>
              {t('txt_plus_help')}
            </OpText>
            <div className={styles.sectionContent}>
              <div className={styles.sectionContentItem}>
                <Help />
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default PlusMembershipPage;
